<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    v-intersection="handleIntersection"
    class="full-width"
  >
    <MentionCard
      ref="mentionCard"
      :action-sheet-component="actionSheetComponent"
      :always-show-keywords="alwaysShowKeywords"
      class="scoped"
      :class="{ 'tw:cursor-pointer': showCursorPointer }"
      :list-title="listTitle"
      :logo="mention.source ? mention.source.logo_url : null"
      :mention="mention"
      :mentions="mentions"
      :no-highlighting="noHighlighting"
      :options="options"
      :selected="selected"
      :show-checkbox="showCheckbox"
      :stream="stream"
      :title="mention.title"
      @click="mentionClicked(mention)"
      @mention-removed="removeMention"
      @mention-selected="$emit('mention-selected', $event)"
    >
      <template #header>
        <BroadcastContentMentionCardHeader
          :custom-header-class="customHeaderClass"
          :mention="mention"
          :mention-card-ref="$refs.mentionCard?.$el"
          :stream="stream"
          @mention-clicked="mentionClicked(mention)"
          @source-name-click="handleOpenOutletSidebar(mention.source)"
        />
      </template>

      <slot />

      <template #list-title-after>
        <div
          v-if="hasBroadcastArchiveRequest"
          class="tw:mb-1"
        >
          <ArchivedContentLabel :mention="mention" />
        </div>
      </template>

      <template
        v-if="showInlinePlaybackControl"
        #beforeBody="{ mentionFormatted }"
      >
        <div
          class="tw:mb-4"
          @click.stop.prevent
        >
          <UniversalPlayerControlWidget
            v-if="!hasPortalContent"
            :clip="mentionFormatted"
            data-testid="radio-mention-universal-player"
            new-design
            @play="playInPopout"
          />

          <PortalTarget
            multiple
            :name="portalName"
            @change="handlePortalChange"
          />
        </div>
      </template>

      <template #body="{ mentionFormatted }">
        <div class="row no-wrap justify-start">
          <MentionExcerpts
            expandable
            :mention="mentionFormatted"
            :no-highlighting="noHighlighting"
          />
        </div>
      </template>

      <template #list-body-after>
        <div
          v-if="hasArchivedBroadcastContent"
          @click.stop
        >
          <div class="tw:mt-2">
            <FullBroadcastAnchor
              font-size="xs"
              :mention="mention"
            />
          </div>
        </div>

        <div
          v-if="isTvEyesContent && isExpired"
          class="tw:mt-2"
        >
          <ExpiredContentNotice />
        </div>
      </template>

      <template #afterBody>
        <div @click.stop>
          <div
            v-if="hasArchivedBroadcastContent"
            class="tw:mt-4"
          >
            <FullBroadcastAnchor
              font-size="md"
              :mention="mention"
            />
          </div>

          <div
            v-if="isTvEyesContent && isExpired"
            class="tw:mt-4"
          >
            <ExpiredContentNotice />
          </div>
        </div>

        <MentionCardInsights
          :mention-title="mention.title"
          :visible-widgets="visibleWidgets"
          :widget-keys="[sentimentWidgetKey]"
          @click.stop.prevent
        >
          <template #components>
            <BroadcastAudienceWidget
              dense
              medium="radio"
              :mention="mention"
            />

            <AdvertisingValueWidget
              dense
              :mention="mention"
            />

            <SentimentWidget
              :key="sentimentWidgetKey"
              dense
              :mention="mention"
              :stream="stream"
            />

            <ReactScoreWidget
              dense
              :is-parent-visible="isIntersecting"
              :mention="mention"
            />

            <ImpactScoreWidget
              dense
              :impact-score="mention.impact_score"
            />
          </template>
        </MentionCardInsights>
      </template>

      <template #list-actions>
        <UniversalPlayerControlWidget
          v-if="showPopUpPlayerWidget"
          class="play-button"
          :clip="mention"
          data-testid="radio-mention-universal-player"
          @play="playInPopout"
        />
      </template>
    </MentionCard>
  </div>
</template>

<script>
import { storeToRefs } from "pinia";
import { PortalTarget } from "portal-vue";
import { ref } from "vue";

import AdvertisingValueWidget from "shared/components/AdvertisingValueWidget.vue";
import BroadcastAudienceWidget from "shared/components/BroadcastAudienceWidget.vue";
import ImpactScoreWidget from "shared/components/ImpactScoreWidget.vue";
import { MentionCard, propsExcept } from "shared/components/MentionCard";
import MentionCardInsights from "shared/components/MentionCardInsights.vue";
import MentionExcerpts from "shared/components/MentionExcerpts.vue";
import { UniversalPlayerControlWidget } from "shared/components/players/UniversalPlayerControlWidget";
import ReactScoreWidget from "shared/components/ReactScoreWidget.vue";
import SentimentWidget from "shared/components/SentimentWidget.vue";
import useMention from "shared/composables/useMention";
import { usePortalContent } from "shared/composables/usePortalContent";
import useSidebarOnMentionCard from "shared/composables/useSidebarOnMentionCard";
import { POPOUT_PLAYER_TARGET } from "shared/constants";
import { isBroadcastProviderUnsupported } from "shared/helpers/broadcastProvider";
import { isTveyesExpired } from "shared/helpers/date";
import { IsPublicInsightsSymbol } from "shared/modules/Charts/types/symbols";
import {
  hasArchivedBroadcastContent,
  hasBroadcastArchiveRequest,
} from "shared/services/mention";
import { useUniversalPlayerStore } from "shared/stores/universalPlayer";
import {
  isKinetiqMention,
  isTvEyesMention,
} from "shared/types/mentions/guards";

import BroadcastContentMentionCardHeader from "./BroadcastContentMentionCard/BroadcastContentMentionCardHeader.vue";
import {
  ArchivedContentLabel,
  ExpiredContentNotice,
  FullBroadcastAnchor,
} from "./BroadcastContentMentionCard";

export default {
  name: "RadioMentionCard",
  components: {
    BroadcastContentMentionCardHeader,
    AdvertisingValueWidget,
    BroadcastAudienceWidget,
    MentionCard,
    MentionCardInsights,
    MentionExcerpts,
    PortalTarget,
    ReactScoreWidget,
    SentimentWidget,
    UniversalPlayerControlWidget,
    ExpiredContentNotice,
    ArchivedContentLabel,
    FullBroadcastAnchor,
    ImpactScoreWidget,
  },
  inject: {
    isPublicInsights: {
      from: IsPublicInsightsSymbol,
      default: false,
    },
  },
  props: {
    ...propsExcept(["listTitle"]),
  },
  emits: ["click", "mention-selected", "mention-removed", "contact-clicked"],
  setup(mentionProps, context) {
    const mentionCard = ref();

    const universalPlayerStore = useUniversalPlayerStore();

    const {
      playerOpen,
      playerPopoutRef,
      playerInitialClip,
      playerIsDismissed,
    } = storeToRefs(universalPlayerStore);

    const {
      playerInsertClip,
      playerShow,
      setPlayerDismissed,
      setPlayerPopoutTarget,
    } = universalPlayerStore;

    const { mentionClicked } = useMention(mentionProps, context);

    const { portalName, hasPortalContent, handlePortalChange } =
      usePortalContent("radio-card");

    const { isOutletEnabled, handleOpenOutletSidebar } =
      useSidebarOnMentionCard({
        emit: context.emit,
      });

    return {
      mentionCard,
      playerOpen,
      playerPopoutRef,
      playerInitialClip,
      playerIsDismissed,

      setPlayerPopoutTarget,
      playerInsertClip,
      playerShow,
      setPlayerDismissed,

      mentionClicked,

      portalName,
      hasPortalContent,
      handlePortalChange,
      isOutletEnabled,
      handleOpenOutletSidebar,
    };
  },
  data() {
    return {
      isIntersecting: false,
      sentimentValue: null,
    };
  },
  computed: {
    customHeaderClass() {
      if (this.isOutletEnabled(this.mention.source)) {
        return ["tw:hover:underline tw:hover:text-blue-500"];
      }

      return [];
    },
    isExpired() {
      return isTveyesExpired(this.mention);
    },
    hasArchivedBroadcastContent() {
      return hasArchivedBroadcastContent(this.mention);
    },
    hasBroadcastArchiveRequest() {
      return hasBroadcastArchiveRequest(this.mention);
    },
    isTvEyesContent() {
      return isTvEyesMention(this.mention);
    },
    sentimentWidgetKey() {
      return `sentiment-widget-${this.mention.id}-${this.sentimentValue}`;
    },
    canShowReactScore() {
      return (
        this.mention.factmata_enrichment &&
        this.$features.has("has_react_score")
      );
    },
    visibleWidgets() {
      const widgets = ["SentimentWidget"];

      if (this.mention.audience) {
        widgets.push("BroadcastAudienceWidget");
      }

      if (
        this.mention.advertising_value &&
        this.$features.has("advertising_value_mentions")
      ) {
        widgets.push("AdvertisingValueWidget");
      }

      if (this.canShowReactScore) widgets.push("ReactScoreWidget");

      if (this.mention.impact_score?.grade) {
        widgets.push("ImpactScoreWidget");
      }

      return widgets;
    },
    listTitle() {
      const mentionTitle = this.mention.program_airing
        ? this.mention.program_airing.name
        : this.mention.source.name;

      return `${mentionTitle} ${this.mention.source.location}`;
    },
    isListView() {
      return Boolean(this.options?.list);
    },
    showCursorPointer() {
      return !this.showPopUpPlayerWidget;
    },
    showInlinePlaybackControl() {
      return this.showNativePlaybackControl && !this.isPublicInsights;
    },
    showNativePlaybackControl() {
      return (
        !this.isTvEyesContent &&
        !isKinetiqMention(this.mention) &&
        !isBroadcastProviderUnsupported(this.mention)
      );
    },
    showPopUpPlayerWidget() {
      return this.showNativePlaybackControl && !this.$isAdminMode;
    },
  },
  watch: {
    "mention.sentiment_ratings": {
      deep: true,
      handler(options) {
        if (options?.length) {
          this.sentimentValue = options[0].sentiment;
        }
      },
    },
  },
  mounted() {
    this.mention.sentiment_ratings ??= [];
  },
  beforeUnmount() {
    if (
      this.isPopoutPlayerPlayingThisClip() &&
      this.playerOpen &&
      this.$isDesktop
    ) {
      this.setPlayerPopoutTarget(POPOUT_PLAYER_TARGET);

      this.$track("Playing the clip in the popout player", {
        type: "radio",
      });
    }
  },
  methods: {
    removeMention(mention) {
      this.$emit("mention-removed", mention);
    },
    handleIntersection(el) {
      this.isIntersecting = el.isIntersecting;
      if (this.playerClosed()) return;
      if (!this.playerOpen) this.playerShow();
      this.setPlayerTarget();
    },
    playInPopout() {
      this.playerInsertClip(this.mention);

      if (!this.playerOpen) this.playerShow();

      this.$nextTick(() => {
        this.playerPopoutRef.playNow(this.mention);
      });

      this.setPlayerTarget();
      this.setPlayerDismissed(false);
    },
    setPlayerTarget() {
      if (this.playInCard()) {
        this.setPlayerPopoutTarget(this.portalName);

        this.$track("Playing the clip in the card", {
          type: "radio",
        });
      } else {
        this.setPlayerPopoutTarget(POPOUT_PLAYER_TARGET);

        this.$track("Playing the clip in the popout player", {
          type: "radio",
        });
      }
    },
    playInCard() {
      return (
        (this.isIntersecting && !this.isListView && !this.alwaysPlayInPopout) ||
        this.$isMobile
      );
    },
    isPopoutPlayerPlayingThisClip() {
      return (
        this.playerInitialClip &&
        this.mention &&
        this.playerInitialClip.id === this.mention.id
      );
    },
    playerClosed() {
      return !this.isPopoutPlayerPlayingThisClip() || this.playerIsDismissed;
    },
  },
};
</script>
